import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { UNSAFE_LocationContext, useLocation, useNavigationType, useOutlet } from 'react-router-dom'
import type { Location, NavigationType } from 'react-router-dom'
import { useTabsStore, EXCLUDED_TAB_PATHS } from '../store/tabs'

interface CachedPane {
  element: ReactNode
  location: Location
  navigationType: NavigationType
}

export interface KeepAliveOutletProps {
  /** 当前登录用户名，用于 localStorage 持久化 key */
  username: string
  /** 根据路由 path 解析页签标题 */
  getTitle: (path: string) => string
}

/**
 * 带页面缓存（KeepAlive）的 Outlet：
 * - 每个已打开的页签对应的路由组件保持挂载，切换时用 display:none 隐藏，
 *   从而保留组件内部状态（表单未提交数据、局部滚动位置）。
 * - 每个缓存面板冻结自己打开时的 Location，避免隐藏页面响应后续路由变化。
 * - 关闭页签后对应缓存被剔除，组件随之卸载。
 * antd 的 Modal/Form 通过 Portal 挂载到 body，不受 display:none 影响。
 */
function KeepAliveOutlet({ username, getTitle }: KeepAliveOutletProps) {
  const location = useLocation()
  const navigationType = useNavigationType()
  const outlet = useOutlet()
  const scrollRef = useRef<Map<string, number>>(new Map())
  const tabs = useTabsStore((s) => s.tabs)
  const init = useTabsStore((s) => s.init)
  const openTab = useTabsStore((s) => s.openTab)

  const path = location.pathname

  // 使用 useState 管理缓存
  // 初始化时如果 outlet 存在，立即放入缓存（确保首次渲染有内容）
  const [cache, setCache] = useState<Map<string, CachedPane>>(() => {
    const initial = new Map<string, CachedPane>()
    if (outlet) {
      initial.set(path, { element: outlet, location, navigationType })
    }
    return initial
  })

  // 当 outlet/path/location 变化时更新缓存
  useEffect(() => {
    if (!outlet) return
    setCache((prev) => {
      const existing = prev.get(path)
      // 避免不必要的更新（防止无限循环）
      // 注意：React 元素每次渲染都是新引用，不能直接用 === 比较
      // 只要 path/location/navigationType 相同，就认为是同一个页面，无需更新
      if (
        existing &&
        existing.location.pathname === location.pathname &&
        existing.location.search === location.search &&
        existing.location.hash === location.hash &&
        existing.navigationType === navigationType
      ) {
        return prev
      }
      const next = new Map(prev)
      next.set(path, { element: outlet, location, navigationType })
      return next
    })
  }, [outlet, path, location, navigationType])

  // 防止浏览器标签页切换导致的白屏：定期检查并修复空 outlet
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !outlet) {
        // 强制重新渲染
        setCache((prev) => new Map(prev))
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [outlet])

  // 路由变化同步到 Tabs：初始化（恢复持久化状态）后打开/激活当前页签
  useEffect(() => {
    init(username)
    // 根路径等排除路径不生成页签（但仍完成初始化）
    if (!EXCLUDED_TAB_PATHS.includes(path)) {
      openTab({ key: path, title: getTitle(path), path })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, path])

  // 保存/恢复各页签的窗口滚动位置
  useEffect(() => {
    const saved = scrollRef.current.get(path)
    window.scrollTo(0, saved ?? 0)
    return () => {
      scrollRef.current.set(path, window.scrollY)
    }
  }, [path])

  // 剔除已关闭页签的缓存，组件随之卸载
  const openKeys = new Set(tabs.map((t) => t.key))
  const filteredCache = new Map(
    [...cache.entries()].filter(([key]) => openKeys.has(key))
  )

  return (
    <>
      {[...filteredCache.entries()].map(([key, pane]) => (
        <div key={key} style={{ display: key === path ? 'block' : 'none' }}>
          <UNSAFE_LocationContext.Provider value={{ location: pane.location, navigationType: pane.navigationType }}>
            {pane.element}
          </UNSAFE_LocationContext.Provider>
        </div>
      ))}
    </>
  )
}

export default KeepAliveOutlet
